import { useState, type ReactNode } from "react";
import ChevronLeft from "react-feather/dist/icons/chevron-left";
import ChevronRight from "react-feather/dist/icons/chevron-right";
import PlusCircle from "react-feather/dist/icons/plus-circle";
import MinusCircle from "react-feather/dist/icons/minus-circle";
import classnames from "classnames";

import style from "./style.module.scss";
import Cover from "./cover";
import Page from "./page";
import { Button } from "../../../ui";
import { range } from "../../../utils";
import type {
  PhotoBookOptions,
  PageOptions,
  ProductEditProps,
  ProductPreviewProps,
} from "../../types";

export function ProductEdit({
  productId,
  userId,
  options,
  setOptions,
}: ProductEditProps<PhotoBookOptions>) {
  const addTwoPages = () =>
    setOptions({
      countPages: options.countPages + 2,
    });
  const removeTwoPages = () => {
    setOptions({
      countPages: options.countPages - 2,
    });
  };
  return (
    <div className={style.product}>
      <div className={style.step}>
        <div className={style.container}>
          {range(options.countPages / 2 + 1).map((index) => (
            <div key={index} className={style.rowContainer}>
              {range(2).map((subIndex) => {
                const pageIndex = index * 2 + subIndex - 1;
                const page = options.pages[pageIndex];
                if (pageIndex < 0 || pageIndex >= options.countPages) {
                  return (
                    <div
                      key={subIndex}
                      className={classnames(style.editEmptyPage, style.emptyPage)}
                    />
                  );
                }
                return (
                  <Page
                    key={subIndex}
                    page={page}
                    productId={productId}
                    userId={userId}
                    index={pageIndex + 1}
                    count={options.countPages}
                    onChange={(page: PageOptions) => {
                      setOptions({
                        pages: {
                          ...options.pages,
                          [pageIndex]: page,
                        },
                      });
                    }}
                  />
                );
              })}
            </div>
          ))}

          <div className={style.addRemoveButtons}>
            <Button
              title="Supprimer deux pages"
              onClick={removeTwoPages}
              isDisabled={options.countPages <= 20}
              isPrimary
            >
              <MinusCircle />
              <span className={style.addRemoveButtonsLabel}>Supprimer deux pages</span>
            </Button>
            <Button title="Ajouter deux pages" onClick={addTwoPages} isPrimary>
              <PlusCircle />
              <span className={style.addRemoveButtonsLabel}>Ajouter deux pages</span>
            </Button>

            <div className={style.addPagesPrice}>(250 DA par page)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProductPreview({ options }: ProductPreviewProps<PhotoBookOptions>) {
  const [current, setCurrent] = useState(0);

  return (
    <div className={style.preview}>
      {current === 0 && (
        <div key="cover" className={style.previewCoverContainer}>
          <Cover.Preview className={style.previewCover} options={options} />
        </div>
      )}
      {current !== 0 && (
        <div className={style.rowContainer}>
          {range(2).map((subIndex) => {
            const pageIndex = (current - 1) * 2 + subIndex - 1;
            const page = options.pages[pageIndex];
            if (pageIndex < 0 || pageIndex >= options.countPages) {
              return <div key={subIndex} className={style.emptyPage} />;
            }
            return <Page.Preview key={subIndex} page={page} />;
          })}
        </div>
      )}
      <div className={style.previewButtons}>
        <Button
          isPrimary
          onClick={() => setCurrent(current === 0 ? options.countPages / 2 + 1 : current - 1)}
        >
          <ChevronLeft />
        </Button>
        <Button
          isPrimary
          onClick={() => setCurrent(current === options.countPages / 2 + 1 ? 0 : current + 1)}
        >
          <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

export function ProductThumbnail(genericOptions: PhotoBookOptions): ReactNode {
  return <Cover.Preview options={genericOptions} className={style.thumbnail} />;
}
